import { useState, useEffect, useRef, useMemo } from "react";
import "./Timeline.css";
import { Container } from "react-bootstrap";
import { primaryColor } from "../../../Constants/Colors";
import { useGetTimelineQuery } from "../../../redux/api/TimelineApi/timelineApi";

// Custom Timeline Components
const VerticalTimeline = ({ children, lineColor = "#E4E7EB" }) => {
  return (
    <div className="vertical-timeline" style={{ "--line-color": lineColor }}>
      {children}
    </div>
  );
};

const VerticalTimelineElement = ({
  className = "",
  contentStyle = {},
  contentArrowStyle = {},
  date,
  iconStyle = {},
  icon,
  position = "right",
  image = null,
  imageAlt = "",
  children,
}) => {
  return (
    <div className={`vertical-timeline-element ${className} ${position}`}>
      <div className="vertical-timeline-element-icon" style={iconStyle}>
        {icon}
      </div>
      <div className="vertical-timeline-element-content" style={contentStyle}>
        <div
          className="vertical-timeline-element-content-arrow"
          style={contentArrowStyle}
        />
        <div className="vertical-timeline-element-date">{date}</div>
        {children}
      </div>
      {image && (
        <div className="vertical-timeline-element-image">
          <img src={image} alt={imageAlt} />
        </div>
      )}
    </div>
  );
};

// Enhanced Timeline Icon Components
const TimelineIcons = {
  Foundation: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
    </svg>
  ),
  Client: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-6h3v-2c0-2.21 1.79-4 4-4s4 1.79 4 4v2h3v6H4z" />
    </svg>
  ),
  Growth: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z" />
    </svg>
  ),
  Award: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M5,16L3,5H1L3,16H5M19,16H21L23,5H21L19,16M6,18C6,19.1 6.9,20 8,20S10,19.1 10,18H20C20,19.1 20.9,20 22,20S24,19.1 24,18H14.5L13,16H8L6.5,18H6Z" />
    </svg>
  ),
  Global: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,19.93C7.05,19.44 4,16.08 4,12C4,11.38 4.08,10.78 4.21,10.21L9,15V16A1,1 0 0,0 10,17H11V19.93M19.9,17.18C19.64,16.83 19.26,16.64 18.84,16.64H18V14A1,1 0 0,0 17,13H13V11H14A1,1 0 0,0 15,10V9H17A2,2 0 0,0 19,7V4.26C19.81,5.53 20.3,7 20.4,8.58L19.9,17.18Z" />
    </svg>
  ),
  Digital: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M4,6H20V16H4M20,18A2,2 0 0,0 22,16V6C22,4.89 21.1,4 20,4H4C2.89,4 2,4.89 2,6V16A2,2 0 0,0 4,18H0V20H24V18H20Z" />
    </svg>
  ),
  Sustainability: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z" />
    </svg>
  ),
  Leadership: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.83 16.28,18.08 12.8,18.82V21H11.2V18.82C7.72,18.08 5,14.83 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z" />
    </svg>
  ),
};

const Timeline = () => {
  const [visibleElements, setVisibleElements] = useState(new Set());
  const timelineRef = useRef(null);

  const { data, isLoading } = useGetTimelineQuery({
    sortBy: "timelineDate",
    order: "desc",
    limit: 100, // Fetch all active milestones for the timeline
  });

  const timelineData = useMemo(() => {
    return data?.timelineData?.filter((item) => item.status) || [];
  }, [data]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    if (isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.getAttribute("data-id");
            if (elementId) {
              setVisibleElements((prev) => new Set([...prev, elementId]));
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: "50px" },
    );

    const timelineElements = document.querySelectorAll(
      ".vertical-timeline-element",
    );
    timelineElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [isLoading, timelineData]);

  // Format date function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Get icon component
  const getIconComponent = (iconName) => {
    const IconComponent = TimelineIcons[iconName] || TimelineIcons.Foundation;
    return <IconComponent />;
  };

  if (isLoading) {
    return (
      <div className="timeline-container">
        <div className="timeline-loading">
          <div className="timeline-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <Container>
      {/* Header Section */}
      <div>
        <h1 style={{ color: primaryColor }}>Our History</h1>
        <p>
          From humble beginnings to digital pioneers – here’s how Ideal evolved
          over the years.
        </p>
      </div>
      <div className="timeline-container">
        {/* Timeline Section */}
        <div
          ref={timelineRef}
          style={{ position: "relative", zIndex: 1, margin: "0 auto" }}
        >
          <VerticalTimeline lineColor="#E4E7EB">
            {timelineData.map((timeline) => (
              <VerticalTimelineElement
                key={timeline.id}
                data-id={timeline.id}
                className={`vertical-timeline-element--work ${visibleElements.has(timeline.id.toString()) ? "animate-in" : ""}`}
                position={timeline.position}
                contentStyle={{
                  background: "rgba(255, 255, 255, 0.95)",
                  color: "#000000",
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "16px",
                }}
                contentArrowStyle={{
                  borderRight:
                    timeline.position === "right"
                      ? "7px solid rgba(255, 255, 255, 0.95)"
                      : "none",
                  borderLeft:
                    timeline.position === "left"
                      ? "7px solid rgba(255, 255, 255, 0.95)"
                      : "none",
                }}
                date={formatDate(timeline.timeline_date)}
                iconStyle={{
                  background: `linear-gradient(135deg, ${timeline.color} 0%, var(--secondary-color) 100%)`,
                  color: "#fff",
                  boxShadow: `0 0 0 6px rgba(255, 255, 255, 1), 0 0 0 8px ${timeline.color}, 0 10px 15px -3px rgba(0, 0, 0, 0.1)`,
                }}
                icon={getIconComponent(timeline.icon)}
                image={timeline.image}
                imageAlt={timeline.imageAlt}
              >
                <div className="d-flex align-items-start gap-3">
                  <h4 className="vertical-timeline-element-step d-inline p-2 rounded-5 text-white">
                    {timeline.timeline_step}
                  </h4>
                  <h1 className="vertical-timeline-element-subtitle fw-bolder">
                    {timeline.timeline_year}
                  </h1>
                </div>

                <h3 className="vertical-timeline-element-title">
                  {timeline.timeline_title}
                </h3>
                <p className="vertical-timeline-element-details">
                  {timeline.timeline_description}
                </p>
              </VerticalTimelineElement>
            ))}
          </VerticalTimeline>
        </div>
      </div>
    </Container>
  );
};

export default Timeline;
